import axios from 'axios';

import {
  getBinanceTicker24h,
  getBingxTicker24h,
  getBitgetTicker,
  getBitmartTicker,
  getBybitTicker,
  getCoinstoreTickers,
  getGateTickers,
  getHotcoinTicker,
  getHtxTicker,
  getIndodaxTickers,
  getKrakenTicker,
  getKucoinStats24h,
  getLbankBookTicker,
  getLbankTicker24h,
  getMexcTicker24h,
  getOkxTicker,
  getWhitebitTickerV1,
  getXtTicker,
  metricsProxyRestOptions,
  proxyRestOptions,
} from '@/shared/cex-sdk';
import { formatBiconomySymbol, getBiconomyTickers } from '@/shared/cex-sdk/exchanges/biconomy/rest';
import { formatWeexSymbol, getWeexTicker } from '@/shared/cex-sdk/exchanges/weex/rest';

import { mapError } from 'api/apiBase/mapError';
import { mapResponse } from 'api/apiBase/mapResponse';
import { ECexName } from 'web3/cexs';

import { calculateSpreadPercentage } from '../lib/spread-calculation';
import { ITickerApiResponse } from '../model';

import { GetCexTickerRequest, GetCexTickerResponse } from './api.types';

/**
 * Symbol format helpers for different exchanges
 */
const formatSymbol = {
  /** Remove slash for exchanges like MEXC, Binance (BTC/USDT -> BTCUSDT) */
  noSlash: (symbol: string) => symbol.replace('/', '').replace('-', ''),
  /** Uppercase with underscore for exchanges like Gate, Bitmart (BTC/USDT -> BTC_USDT) */
  underscore: (symbol: string) => symbol.replace('/', '_').replace('-', '_'),
  /** Lowercase with underscore for exchanges like LBank, XT (BTC/USDT -> btc_usdt) */
  lowerUnderscore: (symbol: string) => symbol.replace('/', '_').replace('-', '_').toLowerCase(),
  /** Hyphen separator for exchanges like OKX, BingX (BTC/USDT -> BTC-USDT) */
  hyphen: (symbol: string) => symbol.replace('/', '-'),
  /** Lowercase concatenation for HTX (BTC/USDT -> btcusdt) */
  lowerNoSlash: (symbol: string) => symbol.replace('/', '').replace('-', '').toLowerCase(),
};

/**
 * Calculate 24h price change percentage from open and last price
 */
const calculatePriceChange = (last_price: string, openPrice: string): string => {
  const last = parseFloat(last_price);
  const open = parseFloat(openPrice);
  if (open === 0) return '0';
  return (((last - open) / open) * 100).toFixed(2);
};

/**
 * Fallback ticker fetchers for each exchange.
 * Each function fetches ticker data directly from the exchange API and maps it to ITickerData.
 */
const fallbackFetchers: Record<
  ECexName,
  (req: GetCexTickerRequest) => Promise<GetCexTickerResponse>
> = {
  // +
  [ECexName.mexc]: async req => {
    const symbol = formatSymbol.noSlash(req.symbol);
    const response = await getMexcTicker24h({ symbol }, proxyRestOptions);

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: response.lastPrice,
        change24h: response.priceChangePercent,
        high24h: response.highPrice,
        low24h: response.lowPrice,
        base_volume: response.volume,
        quote_volume: response.quoteVolume,
        spread_percentage: calculateSpreadPercentage(response.askPrice, response.bidPrice) || '0',
      },
    });
  },

  // +
  [ECexName.binance]: async req => {
    const symbol = formatSymbol.noSlash(req.symbol);
    const response = await getBinanceTicker24h({ symbol }, proxyRestOptions);
    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: response.lastPrice,
        change24h: response.priceChangePercent,
        high24h: response.highPrice,
        low24h: response.lowPrice,
        base_volume: response.volume,
        quote_volume: response.quoteVolume,
        spread_percentage: calculateSpreadPercentage(response.askPrice, response.bidPrice) || '0',
      },
    });
  },

  // +
  [ECexName.gate]: async req => {
    const currency_pair = formatSymbol.underscore(req.symbol);
    const response = await getGateTickers({ currency_pair }, proxyRestOptions);

    if (!response[0]) {
      throw new Error('No ticker data returned from Gate.io');
    }

    const ticker = response[0];
    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.last,
        change24h: ticker.change_percentage,
        high24h: ticker.high_24h,
        low24h: ticker.low_24h,
        base_volume: ticker.base_volume,
        quote_volume: ticker.quote_volume,
        spread_percentage: calculateSpreadPercentage(ticker.lowest_ask, ticker.highest_bid) || '0',
      },
    });
  },

  // +
  [ECexName.kucoin]: async req => {
    const symbol = formatSymbol.hyphen(req.symbol);
    const response = await getKucoinStats24h({ symbol }, proxyRestOptions);

    if (!response.data) {
      throw new Error('No ticker data returned from KuCoin');
    }

    const ticker = response.data;
    // KuCoin changeRate is decimal (0.0015 = 0.15%), convert to percentage
    const changePercent = (parseFloat(ticker.changeRate) * 100).toFixed(2);

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.last,
        change24h: changePercent,
        high24h: ticker.high,
        low24h: ticker.low,
        base_volume: ticker.vol,
        quote_volume: ticker.volValue,
        spread_percentage: calculateSpreadPercentage(ticker.sell, ticker.buy) || '0',
      },
    });
  },

  // +
  [ECexName.bybit]: async req => {
    const symbol = formatSymbol.noSlash(req.symbol);
    const response = await getBybitTicker({ category: 'spot', symbol }, proxyRestOptions);

    if (!response.result?.list?.[0]) {
      throw new Error('No ticker data returned from Bybit');
    }

    const ticker = response.result.list[0];
    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.lastPrice,
        change24h: ticker.price24hPcnt,
        high24h: ticker.highPrice24h,
        low24h: ticker.lowPrice24h,
        base_volume: ticker.volume24h,
        quote_volume: ticker.turnover24h,
        spread_percentage: calculateSpreadPercentage(ticker.ask1Price, ticker.bid1Price) || '0',
      },
    });
  },

  // +
  [ECexName.htx]: async req => {
    const symbol = formatSymbol.lowerNoSlash(req.symbol);
    const response = await getHtxTicker({ symbol }, proxyRestOptions);

    if (!response.tick) {
      throw new Error('No ticker data returned from HTX');
    }

    const ticker = response.tick;
    const change24h = calculatePriceChange(ticker.close.toString(), ticker.open.toString());

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.close.toString(),
        change24h,
        high24h: ticker.high.toString(),
        low24h: ticker.low.toString(),
        base_volume: ticker.amount.toString(),
        quote_volume: ticker.vol.toString(),
        spread_percentage:
          calculateSpreadPercentage(ticker.ask.toString(), ticker.bid.toString()) || '0',
      },
    });
  },

  // +
  [ECexName.okx]: async req => {
    const instId = formatSymbol.hyphen(req.symbol);
    const response = await getOkxTicker({ instId }, proxyRestOptions);

    if (!response.data?.[0]) {
      throw new Error('No ticker data returned from OKX');
    }

    const ticker = response.data[0];
    const change24h = calculatePriceChange(ticker.last, ticker.open24h);

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.last,
        change24h,
        high24h: ticker.high24h,
        low24h: ticker.low24h,
        base_volume: ticker.vol24h,
        quote_volume: ticker.volCcy24h,
        spread_percentage: calculateSpreadPercentage(ticker.askPx, ticker.bidPx) || '0',
      },
    });
  },

  // +
  [ECexName.weex]: async req => {
    // WEEX uses the legacy "_SPBL" symbol suffix and returns ticker `data` as an object.
    const [base, quote] = req.symbol.split(/[/-]/);
    const symbol = formatWeexSymbol(base ?? '', quote ?? '');
    const response = await getWeexTicker({ symbol }, proxyRestOptions);

    if (response.code !== '00000' || !response.data) {
      throw new Error('No ticker data returned from WEEX');
    }

    const ticker = response.data;
    const change24h =
      ticker.priceChangePercent ?? calculatePriceChange(ticker.lastPrice, ticker.open);

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.lastPrice,
        change24h,
        high24h: ticker.high,
        low24h: ticker.low,
        base_volume: ticker.size,
        quote_volume: ticker.value,
        spread_percentage: '0',
      },
    });
  },

  // +
  [ECexName.biconomy]: async req => {
    // Biconomy has no per-symbol ticker endpoint — fetch all tickers and filter by symbol.
    const [base, quote] = req.symbol.split(/[/-]/);
    const symbol = formatBiconomySymbol(base ?? '', quote ?? '');
    const response = await getBiconomyTickers(proxyRestOptions);

    const ticker = response.ticker?.find(t => t.symbol?.toUpperCase() === symbol);
    if (!ticker) {
      throw new Error('No ticker data returned from Biconomy');
    }

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.last,
        // Biconomy `change` is already a 24h percentage.
        change24h: ticker.change,
        high24h: ticker.high,
        low24h: ticker.low,
        base_volume: ticker.vol,
        quote_volume: ticker.deal,
        spread_percentage: calculateSpreadPercentage(ticker.sell, ticker.buy) || '0',
      },
    });
  },

  // +
  [ECexName.bitget]: async req => {
    const symbol = formatSymbol.noSlash(req.symbol);
    const response = await getBitgetTicker({ symbol }, proxyRestOptions);

    if (!response.data?.[0]) {
      throw new Error('No ticker data returned from Bitget');
    }

    const ticker = response.data[0];
    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.lastPr,
        change24h: ticker.change24h,
        high24h: ticker.high24h,
        low24h: ticker.low24h,
        base_volume: ticker.baseVolume,
        quote_volume: ticker.quoteVolume,
        spread_percentage:
          calculateSpreadPercentage(
            ticker.sellOne as string | number,
            ticker.buyOne as string | number,
          ) || '0',
      },
    });
  },

  // +
  [ECexName.bingx]: async req => {
    const symbol = formatSymbol.hyphen(req.symbol);
    const response = await getBingxTicker24h({ symbol }, proxyRestOptions);
    const ticker = response.data;

    if (!ticker) {
      throw new Error('No ticker data returned from BingX');
    }

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.lastPrice.toString(),
        change24h: ticker.priceChange.toString(),
        high24h: ticker.highPrice.toString(),
        low24h: ticker.lowPrice.toString(),
        base_volume: ticker.volume.toString(),
        quote_volume: ticker.quoteVolume.toString(),
        spread_percentage: '0',
      },
    });
  },

  // +
  [ECexName.bitmart]: async req => {
    const symbol = formatSymbol.underscore(req.symbol);
    const response = await getBitmartTicker({ symbol }, proxyRestOptions);

    if (!response.data) {
      throw new Error('No ticker data returned from Bitmart');
    }

    const ticker = response.data;
    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.last_price,
        change24h: ticker.fluctuation,
        high24h: ticker.high_24h,
        low24h: ticker.low_24h,
        base_volume: ticker.base_volume_24h,
        quote_volume: ticker.quote_volume_24h,
        spread_percentage: calculateSpreadPercentage(ticker.best_ask, ticker.best_bid) || '0',
      },
    });
  },

  // +
  [ECexName.xt]: async req => {
    const symbol = formatSymbol.lowerUnderscore(req.symbol);
    const response = await getXtTicker({ symbol }, proxyRestOptions);

    const ticker = response?.result?.[0];
    if (!ticker) {
      throw new Error('No ticker data returned from XT');
    }

    const change24h = calculatePriceChange(ticker.c, ticker.o);
    // Alternatively use ticker.cr if it's already a percentage string
    // const change24h = (parseFloat(ticker.cr) * 100).toFixed(2);

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.c,
        change24h,
        high24h: ticker.h,
        low24h: ticker.l,
        base_volume: ticker.v,
        quote_volume: ticker.q,
        spread_percentage: calculateSpreadPercentage(ticker.ap, ticker.bp) || '0',
      },
    });
  },

  // +
  [ECexName.lbank]: async req => {
    const symbol = formatSymbol.lowerUnderscore(req.symbol);

    // Fetch both ticker data and book ticker for complete information
    const [tickerResponse, bookTickerResponse] = await Promise.all([
      getLbankTicker24h({ symbol }, proxyRestOptions),
      getLbankBookTicker({ symbol }, proxyRestOptions),
    ]);

    const ticker = tickerResponse?.data?.[0]?.ticker;
    const bookTicker = bookTickerResponse?.data;

    if (!ticker) {
      throw new Error('No ticker data returned from LBank');
    }

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.latest,
        change24h: ticker.change,
        high24h: ticker.high,
        low24h: ticker.low,
        base_volume: ticker.vol,
        quote_volume: ticker.turnover,
        spread_percentage: bookTicker
          ? calculateSpreadPercentage(bookTicker.askPrice, bookTicker.bidPrice) || '0'
          : '0',
      },
    });
  },

  // +
  [ECexName.whitebit]: async req => {
    const market = formatSymbol.underscore(req.symbol);
    const response = await getWhitebitTickerV1({ market }, proxyRestOptions);

    if (!response.result) {
      throw new Error(`No ticker data returned from WhiteBIT for symbol ${market}`);
    }

    const ticker = response.result;

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.last,
        change24h: ticker.change,
        high24h: ticker.high,
        low24h: ticker.low,
        base_volume: ticker.volume,
        quote_volume: ticker.deal,
        spread_percentage: calculateSpreadPercentage(ticker.ask, ticker.bid) || '0',
      },
    });
  },

  // +
  [ECexName.coinstore]: async req => {
    const symbol = formatSymbol.noSlash(req.symbol);
    const response = await getCoinstoreTickers(proxyRestOptions);

    if (!response.data || !Array.isArray(response.data)) {
      throw new Error('No ticker data returned from Coinstore');
    }

    const ticker = response.data.find(t => t.symbol === symbol);

    if (!ticker) {
      throw new Error(`Ticker not found for symbol ${symbol} in Coinstore`);
    }

    const change24h = calculatePriceChange(ticker.close, ticker.open);

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.close,
        change24h,
        high24h: ticker.high,
        low24h: ticker.low,
        base_volume: ticker.volume,
        quote_volume: ticker.amount,
        spread_percentage: calculateSpreadPercentage(ticker.ask, ticker.bid) || '0',
      },
    });
  },

  // +
  [ECexName.indodax]: async req => {
    const symbol = formatSymbol.lowerUnderscore(req.symbol);
    const response = await getIndodaxTickers(proxyRestOptions);

    if (!response.tickers || !response.tickers[symbol]) {
      throw new Error(`Ticker not found for symbol ${symbol} in Indodax`);
    }

    const ticker = response.tickers[symbol];

    // Indodax uses dynamic volume keys (e.g., vol_btc for base, vol_idr for quote for btc_idr pair)
    const [base, quote] = symbol.split('_');
    const baseVolume = ticker[`vol_${base}`]?.toString() || '0';
    const quoteVolume = ticker[`vol_${quote}`]?.toString() || '0';

    // Calculate change based on high and low if not provided (Indodax API doesn't guarantee change field)
    // Actually, Indodax doesn't provide open price in this endpoint, so we can't calculate change24h accurately
    // We'll use 0 or estimate if possible. But wait, ticker response has high/low/last/buy/sell.
    // Let's rely on fallback calculation or just high/low difference which is spread, not change.
    // For now returning '0' for change as it's missing in Indodax public ticker.
    const change24h = '0';

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.last,
        change24h,
        high24h: ticker.high,
        low24h: ticker.low,
        base_volume: baseVolume,
        quote_volume: quoteVolume,
        spread_percentage: calculateSpreadPercentage(ticker.sell, ticker.buy) || '0',
      },
    });
  },

  // -
  [ECexName.hotcoin]: async req => {
    const symbol = formatSymbol.lowerNoSlash(req.symbol);
    const response = await getHotcoinTicker({ symbol });

    if (!response.data) {
      throw new Error('No ticker data returned from Hotcoin');
    }

    const ticker = response.data;
    // Hotcoin doesn't provide quote volume or change percentage directly
    const change24h = '0'; // Would need open price to calculate this

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.last,
        change24h,
        high24h: ticker.high,
        low24h: ticker.low,
        base_volume: ticker.vol,
        quote_volume: '0', // Not provided by Hotcoin API
        spread_percentage: calculateSpreadPercentage(ticker.sell, ticker.buy) || '0',
      },
    });
  },

  // +
  [ECexName.kraken]: async req => {
    // Kraken uses different pair naming (e.g., XBTUSD for BTC/USD)
    // This is a simplified version - may need pair name mapping
    const pair = formatSymbol.noSlash(req.symbol);
    const response = await getKrakenTicker({ pair }, proxyRestOptions);

    const pairData = response.result?.[pair] || response.result?.[Object.keys(response.result)[0]];
    if (!pairData) {
      throw new Error('No ticker data returned from Kraken');
    }

    // Kraken returns arrays: c[0] = last price, h[1] = 24h high, l[1] = 24h low, v[1] = 24h volume, o = open
    const lastPrice = pairData.c[0];
    const change24h = calculatePriceChange(lastPrice, pairData.o);

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: lastPrice,
        change24h,
        high24h: pairData.h[1],
        low24h: pairData.l[1],
        base_volume: pairData.v[1],
        quote_volume: (parseFloat(pairData.v[1]) * parseFloat(pairData.p[1])).toString(), // Estimated
        spread_percentage: calculateSpreadPercentage(pairData.a[0], pairData.b[0]) || '0',
      },
    });
  },
  [ECexName.coinbase]: async req => {
    const product = formatSymbol.hyphen(req.symbol).toUpperCase();
    const [statsResponse, bookResponse] = await Promise.all([
      axios.get<{
        open: string;
        high: string;
        low: string;
        last: string;
        volume: string;
      }>(`https://api.exchange.coinbase.com/products/${product}/stats`),
      axios.get<{ bids: string[][]; asks: string[][] }>(
        `https://api.exchange.coinbase.com/products/${product}/book`,
        { params: { level: 1 } },
      ),
    ]);

    const stats = statsResponse.data;
    const bestBid = bookResponse.data.bids[0]?.[0] ?? '0';
    const bestAsk = bookResponse.data.asks[0]?.[0] ?? '0';

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: stats.last,
        change24h: calculatePriceChange(stats.last, stats.open),
        high24h: stats.high,
        low24h: stats.low,
        base_volume: stats.volume,
        quote_volume: (Number(stats.volume) * Number(stats.last)).toString(),
        spread_percentage: calculateSpreadPercentage(bestAsk, bestBid) || '0',
      },
    });
  },
  [ECexName.cryptocom]: async req => {
    const instrument = formatSymbol.underscore(req.symbol).toUpperCase();
    const [tickerResponse, bookResponse] = await Promise.all([
      axios.get<{
        code: number;
        message?: string;
        result?: {
          data: Array<{
            i: string;
            a: string;
            h: string;
            l: string;
            c: string;
            v: string;
            vv: string;
          }>;
        };
      }>('https://api.crypto.com/exchange/v1/public/get-tickers', {
        params: { instrument_name: instrument },
      }),
      axios.get<{
        code: number;
        message?: string;
        result?: {
          data: Array<{ bids: string[][]; asks: string[][] }>;
        };
      }>('https://api.crypto.com/exchange/v1/public/get-book', {
        params: { instrument_name: instrument, depth: 1 },
      }),
    ]);
    const ticker = tickerResponse.data.result?.data.find(item => item.i === instrument);
    const book = bookResponse.data.result?.data[0];
    if (tickerResponse.data.code !== 0 || bookResponse.data.code !== 0 || !ticker || !book) {
      throw new Error(
        tickerResponse.data.message ??
          bookResponse.data.message ??
          `No ticker data returned from Crypto.com for ${instrument}`,
      );
    }

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.a,
        change24h: (Number(ticker.c) * 100).toString(),
        high24h: ticker.h,
        low24h: ticker.l,
        base_volume: ticker.v,
        quote_volume: ticker.vv,
        spread_percentage:
          calculateSpreadPercentage(book.asks[0]?.[0] ?? '0', book.bids[0]?.[0] ?? '0') || '0',
      },
    });
  },
  [ECexName.upbit]: async req => {
    const [base = '', quote = ''] = req.symbol.toUpperCase().split(/[/-]/);
    const market = `${quote}-${base}`;
    const proxyClient = proxyRestOptions.client;
    if (!proxyClient) throw new Error('Upbit public proxy client is unavailable');
    const [tickerResponse, bookResponse] = await Promise.all([
      proxyClient.get<
        Array<{
          market: string;
          trade_price: number | string;
          high_price: number | string;
          low_price: number | string;
          signed_change_rate: number | string;
          acc_trade_volume_24h: number | string;
          acc_trade_price_24h: number | string;
        }>
      >('https://api.upbit.com/v1/ticker', { params: { markets: market } }),
      proxyClient.get<
        Array<{
          orderbook_units: Array<{
            ask_price: number | string;
            bid_price: number | string;
          }>;
        }>
      >('https://api.upbit.com/v1/orderbook', { params: { markets: market } }),
    ]);
    const ticker = tickerResponse.data[0];
    const best = bookResponse.data[0]?.orderbook_units[0];
    if (!ticker || !best) {
      throw new Error(`No ticker data returned from Upbit for ${market}`);
    }

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: String(ticker.trade_price),
        change24h: (Number(ticker.signed_change_rate) * 100).toString(),
        high24h: String(ticker.high_price),
        low24h: String(ticker.low_price),
        base_volume: String(ticker.acc_trade_volume_24h),
        quote_volume: String(ticker.acc_trade_price_24h),
        spread_percentage:
          calculateSpreadPercentage(String(best.ask_price), String(best.bid_price)) || '0',
      },
    });
  },
  [ECexName.bithumb]: async req => {
    const [base = '', quote = ''] = req.symbol.toUpperCase().split(/[/-]/);
    const market = `${quote}-${base}`;
    const proxyClient = proxyRestOptions.client;
    if (!proxyClient) throw new Error('Bithumb public proxy client is unavailable');
    const [tickerResponse, bookResponse] = await Promise.all([
      proxyClient.get<
        | Array<{
            market: string;
            trade_price: number | string;
            high_price: number | string;
            low_price: number | string;
            signed_change_rate: number | string;
            acc_trade_volume_24h: number | string;
            acc_trade_price_24h: number | string;
          }>
        | { error: { message?: string } }
      >('https://api.bithumb.com/v1/ticker', { params: { markets: market } }),
      proxyClient.get<
        | Array<{
            orderbook_units: Array<{
              ask_price: number | string;
              bid_price: number | string;
            }>;
          }>
        | { error: { message?: string } }
      >('https://api.bithumb.com/v1/orderbook', { params: { markets: market } }),
    ]);
    if ('error' in tickerResponse.data || 'error' in bookResponse.data) {
      const message =
        ('error' in tickerResponse.data ? tickerResponse.data.error.message : undefined) ??
        ('error' in bookResponse.data ? bookResponse.data.error.message : undefined) ??
        `No ticker data returned from Bithumb for ${market}`;
      throw new Error(message);
    }
    const ticker = tickerResponse.data[0];
    const best = bookResponse.data[0]?.orderbook_units[0];
    if (!ticker || !best) {
      throw new Error(`No ticker data returned from Bithumb for ${market}`);
    }

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: String(ticker.trade_price),
        change24h: (Number(ticker.signed_change_rate) * 100).toString(),
        high24h: String(ticker.high_price),
        low24h: String(ticker.low_price),
        base_volume: String(ticker.acc_trade_volume_24h),
        quote_volume: String(ticker.acc_trade_price_24h),
        spread_percentage:
          calculateSpreadPercentage(String(best.ask_price), String(best.bid_price)) || '0',
      },
    });
  },
  [ECexName.bitstamp]: async req => {
    const market = formatSymbol.lowerNoSlash(req.symbol);
    const proxyClient = proxyRestOptions.client;
    if (!proxyClient) throw new Error('Bitstamp public proxy client is unavailable');
    const [tickerResponse, bookResponse] = await Promise.all([
      proxyClient.get<{
        last?: number | string;
        high?: number | string;
        low?: number | string;
        volume?: number | string;
        percent_change_24?: number | string;
      }>(`https://www.bitstamp.net/api/v2/ticker/${market}/`),
      proxyClient.get<{
        asks?: Array<[number | string, number | string]>;
        bids?: Array<[number | string, number | string]>;
      }>(`https://www.bitstamp.net/api/v2/order_book/${market}/`, {
        params: { group: 1 },
      }),
    ]);
    const ticker = tickerResponse.data;
    const book = bookResponse.data;
    const bestAsk = book?.asks?.[0]?.[0];
    const bestBid = book?.bids?.[0]?.[0];
    if (
      !ticker ||
      Array.isArray(ticker) ||
      ticker.last === undefined ||
      ticker.high === undefined ||
      ticker.low === undefined ||
      ticker.volume === undefined ||
      ticker.percent_change_24 === undefined ||
      bestAsk === undefined ||
      bestBid === undefined
    ) {
      throw new Error(`No ticker data returned from Bitstamp for ${market}`);
    }

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: String(ticker.last),
        change24h: String(ticker.percent_change_24),
        high24h: String(ticker.high),
        low24h: String(ticker.low),
        base_volume: String(ticker.volume),
        quote_volume: '',
        spread_percentage: calculateSpreadPercentage(String(bestAsk), String(bestBid)) || '0',
      },
    });
  },
  [ECexName.bitvavo]: async req => {
    const market = formatSymbol.hyphen(req.symbol).toUpperCase();
    const proxyClient = proxyRestOptions.client;
    if (!proxyClient) throw new Error('Bitvavo public proxy client is unavailable');
    const response = await proxyClient.get<
      | {
          market: string;
          open: number | string;
          high: number | string;
          low: number | string;
          last: number | string;
          bid: number | string;
          ask: number | string;
          volume: number | string;
          volumeQuote: number | string;
        }
      | Array<{
          market: string;
          open: number | string;
          high: number | string;
          low: number | string;
          last: number | string;
          bid: number | string;
          ask: number | string;
          volume: number | string;
          volumeQuote: number | string;
        }>
    >('https://api.bitvavo.com/v2/ticker/24h', { params: { market } });
    const items = Array.isArray(response.data) ? response.data : [response.data];
    const ticker = items.find(item => item?.market === market);
    if (!ticker) {
      throw new Error(`No ticker data returned from Bitvavo for ${market}`);
    }

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: String(ticker.last),
        change24h: calculatePriceChange(String(ticker.last), String(ticker.open)),
        high24h: String(ticker.high),
        low24h: String(ticker.low),
        base_volume: String(ticker.volume),
        quote_volume: String(ticker.volumeQuote),
        spread_percentage: calculateSpreadPercentage(String(ticker.ask), String(ticker.bid)) || '0',
      },
    });
  },
  [ECexName.toobit]: async req => {
    const symbol = formatSymbol.noSlash(req.symbol).toUpperCase();
    const proxyClient = proxyRestOptions.client;
    if (!proxyClient) throw new Error('Toobit public proxy client is unavailable');
    const response = await proxyClient.get<
      Array<{
        s: string;
        c: number | string;
        o: number | string;
        h: number | string;
        l: number | string;
        v: number | string;
        qv: number | string;
        a: number | string;
        b: number | string;
      }>
    >('https://api.toobit.com/quote/v1/ticker/24hr', { params: { symbol } });
    const ticker = response.data.find(item => item.s === symbol);
    if (!ticker) {
      throw new Error(`No ticker data returned from Toobit for ${symbol}`);
    }

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: String(ticker.c),
        change24h: calculatePriceChange(String(ticker.c), String(ticker.o)),
        high24h: String(ticker.h),
        low24h: String(ticker.l),
        base_volume: String(ticker.v),
        quote_volume: String(ticker.qv),
        spread_percentage: calculateSpreadPercentage(String(ticker.a), String(ticker.b)) || '0',
      },
    });
  },
  [ECexName.hashkey]: async req => {
    const symbol = formatSymbol.noSlash(req.symbol).toUpperCase();
    const proxyClient = proxyRestOptions.client;
    if (!proxyClient) throw new Error('HashKey Global public proxy client is unavailable');
    const response = await proxyClient.get<
      Array<{
        s: string;
        c: number | string;
        o: number | string;
        h: number | string;
        l: number | string;
        v: number | string;
        qv: number | string;
        a: number | string;
        b: number | string;
      }>
    >('https://api-glb.hashkey.com/quote/v1/ticker/24hr', { params: { symbol } });
    const ticker = response.data.find(item => item.s === symbol);
    if (!ticker) {
      throw new Error(`No ticker data returned from HashKey Global for ${symbol}`);
    }

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: String(ticker.c),
        change24h: calculatePriceChange(String(ticker.c), String(ticker.o)),
        high24h: String(ticker.h),
        low24h: String(ticker.l),
        base_volume: String(ticker.v),
        quote_volume: String(ticker.qv),
        spread_percentage: calculateSpreadPercentage(String(ticker.a), String(ticker.b)) || '0',
      },
    });
  },
  [ECexName.bullish]: async req => {
    const symbol = formatSymbol.noSlash(req.symbol).toUpperCase();
    const proxyClient = metricsProxyRestOptions.client;
    if (!proxyClient) throw new Error('Bullish public proxy client is unavailable');
    const response = await proxyClient.get<{
      symbol: string;
      last: number | string;
      open: number | string;
      high: number | string;
      low: number | string;
      percentage: number | string;
      baseVolume: number | string;
      quoteVolume: number | string;
      bestAsk: number | string;
      bestBid: number | string;
    }>(`https://api.exchange.bullish.com/trading-api/v1/markets/${symbol}/tick`);
    const ticker = response.data;
    if (ticker.symbol?.toUpperCase() !== symbol || ticker.last === undefined) {
      throw new Error(`No ticker data returned from Bullish for ${symbol}`);
    }

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: String(ticker.last),
        change24h:
          ticker.percentage === undefined
            ? calculatePriceChange(String(ticker.last), String(ticker.open))
            : String(ticker.percentage),
        high24h: String(ticker.high),
        low24h: String(ticker.low),
        base_volume: String(ticker.baseVolume),
        quote_volume: String(ticker.quoteVolume),
        spread_percentage:
          calculateSpreadPercentage(String(ticker.bestAsk), String(ticker.bestBid)) || '0',
      },
    });
  },
  [ECexName.gemini]: async req => {
    const symbol = formatSymbol.noSlash(req.symbol).toLowerCase();
    const [base, quote] = req.symbol.replace('-', '/').toUpperCase().split('/');
    if (!base || !quote) {
      throw new Error(`Gemini ticker symbol ${req.symbol} must include base and quote`);
    }
    const proxyClient = proxyRestOptions.client;
    if (!proxyClient) throw new Error('Gemini public proxy client is unavailable');
    const [tickerResponse, volumeResponse] = await Promise.all([
      proxyClient.get<{
        symbol: string;
        open: number | string;
        high: number | string;
        low: number | string;
        close: number | string;
        bid: number | string;
        ask: number | string;
      }>(`https://api.gemini.com/v2/ticker/${symbol}`),
      proxyClient.get<{
        last: number | string;
        volume: Record<string, number | string>;
      }>(`https://api.gemini.com/v1/pubticker/${symbol}`),
    ]);
    const ticker = tickerResponse.data;
    const publicTicker = volumeResponse.data;
    const baseVolume = publicTicker.volume?.[base];
    const quoteVolume = publicTicker.volume?.[quote];
    if (
      ticker.symbol?.toLowerCase() !== symbol ||
      publicTicker.last === undefined ||
      baseVolume === undefined ||
      quoteVolume === undefined
    ) {
      throw new Error(`No complete public ticker data returned from Gemini for ${symbol}`);
    }

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: String(publicTicker.last),
        change24h: calculatePriceChange(String(publicTicker.last), String(ticker.open)),
        high24h: String(ticker.high),
        low24h: String(ticker.low),
        base_volume: String(baseVolume),
        quote_volume: String(quoteVolume),
        spread_percentage: calculateSpreadPercentage(String(ticker.ask), String(ticker.bid)) || '0',
      },
    });
  },
  [ECexName.bitfinex]: async req => {
    const symbol = `t${formatSymbol.noSlash(req.symbol).toUpperCase()}`;
    const proxyClient = proxyRestOptions.client;
    if (!proxyClient) throw new Error('Bitfinex public proxy client is unavailable');
    const response = await proxyClient.get<Array<number | string>>(
      `https://api-pub.bitfinex.com/v2/ticker/${symbol}`,
    );
    const ticker = response.data;
    if (
      !Array.isArray(ticker) ||
      ticker.length < 10 ||
      ticker.slice(0, 10).some(value => !Number.isFinite(Number(value)))
    ) {
      throw new Error(`No complete public ticker data returned from Bitfinex for ${symbol}`);
    }

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: String(ticker[6]),
        change24h: String(Number(ticker[5]) * 100),
        high24h: String(ticker[8]),
        low24h: String(ticker[9]),
        base_volume: String(ticker[7]),
        quote_volume: String(Number(ticker[7]) * Number(ticker[6])),
        spread_percentage: calculateSpreadPercentage(String(ticker[2]), String(ticker[0])) || '0',
      },
    });
  },
  [ECexName.dextrade]: async req => {
    const pair = formatSymbol.noSlash(req.symbol).toUpperCase();
    const [tickerResponse, bookResponse] = await Promise.all([
      axios.get<{
        status: boolean;
        data: {
          last: string | number;
          high: string | number;
          low: string | number;
          volume_24H: string | number;
          percent_change?: string | number;
          percent_сhange?: string | number;
        };
      }>('https://api.dex-trade.com/v1/public/ticker', { params: { pair } }),
      axios.get<{
        status: boolean;
        data: {
          buy: { rate: string | number; volume: string | number; count: number }[];
          sell: { rate: string | number; volume: string | number; count: number }[];
        };
      }>('https://api.dex-trade.com/v1/public/book', { params: { pair } }),
    ]);

    if (!tickerResponse.data.status || !bookResponse.data.status) {
      throw new Error(`No complete public ticker data returned from Dex-Trade for ${pair}`);
    }

    const ticker = tickerResponse.data.data;
    const bid = bookResponse.data.data.buy?.find(level => level.count > 0)?.rate;
    const ask = bookResponse.data.data.sell?.find(level => level.count > 0)?.rate;
    const last = String(ticker.last);
    const baseVolume = String(ticker.volume_24H);

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: last,
        change24h: String(ticker.percent_сhange ?? ticker.percent_change ?? 0),
        high24h: String(ticker.high),
        low24h: String(ticker.low),
        base_volume: baseVolume,
        quote_volume: String(Number(baseVolume) * Number(last)),
        spread_percentage:
          bid !== undefined && ask !== undefined
            ? calculateSpreadPercentage(String(ask), String(bid)) || '0'
            : '0',
      },
    });
  },
  [ECexName.coinw]: async () => {
    throw new Error('No fallback fetcher implemented for CoinW');
  },
};

/**
 * Fallback function to get ticker data directly from exchange APIs
 * when the main API fails.
 */
export default async function getCexTickerFallback(
  req: GetCexTickerRequest,
): Promise<GetCexTickerResponse> {
  try {
    const fetcher = fallbackFetchers[req.cex];

    if (!fetcher) {
      throw new Error(`No fallback fetcher implemented for ${req.cex}`);
    }
    return fetcher(req);
  } catch (error) {
    return mapError<ITickerApiResponse>({ error });
  }
}
